import { SaveOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, message, Typography } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LocationAutocomplete } from '../../components/LocationAutocomplete/LocationAutocomplete';
import { firestore } from '../../firebase/firebase';
import { WantedListing } from '../../models/wantedListings.model';
import { useAuth } from '../Auth/AuthContext';

import { LocationRadiusMap } from './components/LocationRadiusMap';
import { useAddWantedListing, useEditWantedListing } from './hooks/useWantedListings';

const { Text } = Typography;

interface AddWantedListingFormProps {
  onSuccess?: () => void;
  mode?: 'add' | 'edit';
  existingListing?: WantedListing;
}

export const AddWantedListingForm: React.FC<AddWantedListingFormProps> = ({
  onSuccess,
  mode = 'add',
  existingListing,
}) => {
  const [form] = Form.useForm();
  const { currentUser } = useAuth();
  const { t } = useTranslation('wanted-listings');
  const { addListing, loading: addLoading } = useAddWantedListing();
  const { editListing, loading: editLoading } = useEditWantedListing();
  const loading = addLoading || editLoading;

  const [locationCoordinates, setLocationCoordinates] = useState<{ lat: number; lng: number } | undefined>(
    existingListing?.locationCoordinates
  );

  // Ustaw wartości formularza
  React.useEffect(() => {
    if (mode === 'edit' && existingListing) {
      form.setFieldsValue({
        location: existingListing.location,
        locationRadius: existingListing.locationRadius || 5,
        maxRent: existingListing.maxRent,
        minSurface: existingListing.minSurface,
        description: existingListing.description || '',
        companyWebsite: existingListing.companyWebsite || '',
      });

      if (existingListing.locationCoordinates) {
        setLocationCoordinates(existingListing.locationCoordinates);
      }
    }
  }, [currentUser, form, mode, existingListing]);

  const handleSubmit = async (values: any) => {
    if (!currentUser) {
      message.error(t('ERRORS.NOT_LOGGED_IN'));
      return;
    }

    try {
      // Sprawdź limit 10 aktywnych ogłoszeń (tylko przy dodawaniu nowego)
      if (mode === 'add') {
        const activeListingsSnapshot = await firestore
          .collection('wantedListings')
          .where('uid', '==', currentUser.uid)
          .where('status', '==', 'active')
          .get();

        if (activeListingsSnapshot.size >= 10) {
          message.error(t('ERRORS.LISTING_LIMIT_REACHED'));
          return;
        }
      }

      const listingData: any = {
        uid: currentUser.uid,
        location: values.location,
      };

      // Dodaj współrzędne jeśli są dostępne
      if (locationCoordinates) {
        listingData.locationCoordinates = locationCoordinates;
        listingData.locationRadius = values.locationRadius || 5;
      }

      // Dodaj opcjonalne pola tylko jeśli wypełnione
      if (values.maxRent) {
        listingData.maxRent = values.maxRent;
      }

      if (values.minSurface) {
        listingData.minSurface = values.minSurface;
      }

      if (values.description?.trim()) {
        listingData.description = values.description.trim();
      }

      if (values.companyWebsite) {
        listingData.companyWebsite = values.companyWebsite;
      }

      if (mode === 'edit' && existingListing) {
        await editListing(existingListing.id, listingData);
        message.success(t('SUCCESS.LISTING_UPDATED'));
      } else {
        await addListing(listingData);
        message.success(t('SUCCESS.LISTING_ADDED'));
        form.resetFields();
      }

      onSuccess?.();
    } catch (error) {
      message.error(t('ERRORS.GENERIC_ERROR'));
      console.error(error);
    }
  };

  return (
    <div className="flex justify-center w-full">
      <div className="w-full max-w-2xl p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="w-full">
          <Form.Item
            label={t('FORM.LOCATION_LABEL')}
            name="location"
            rules={[
              { required: true, message: t('FORM.LOCATION_REQUIRED') },
              { min: 3, message: t('FORM.LOCATION_MIN_LENGTH') },
            ]}
            extra={
              <Text type="secondary" className="text-xs">
                W jakiej lokalizacji szukasz lokalu?
              </Text>
            }>
            <LocationAutocomplete
              onChange={(_, coordinates) => {
                setLocationCoordinates(coordinates);
              }}
              size="large"
            />
          </Form.Item>

          {locationCoordinates && (
            <>
              <Form.Item
                label={t('FORM.LOCATION_RADIUS_LABEL')}
                name="locationRadius"
                initialValue={5}
                extra={
                  <Text type="secondary" className="text-xs">
                    W jakim promieniu od wybranej lokalizacji szukasz lokalu?
                  </Text>
                }>
                <InputNumber min={1} max={100} addonAfter="km" style={{ width: 150 }} />
              </Form.Item>

              <Form.Item
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.locationRadius !== currentValues.locationRadius
                }>
                {({ getFieldValue }) => {
                  const radius = getFieldValue('locationRadius') || 5;
                  return <LocationRadiusMap center={locationCoordinates} radius={radius} />;
                }}
              </Form.Item>
            </>
          )}

          <Form.Item
            label={t('FORM.MONTHLY_RENT_LABEL')}
            name="maxRent"
            extra={
              <Text type="secondary" className="text-xs">
                Jaki maksymalny czynsz za m² jesteś w stanie zaakceptować?
              </Text>
            }>
            <InputNumber min={0} placeholder="np. 50" className="w-full" addonAfter="PLN/m²" />
          </Form.Item>

          <Form.Item
            label="Minimalna powierzchnia"
            name="minSurface"
            extra={
              <Text type="secondary" className="text-xs">
                Jaka minimalna powierzchnia lokalu Cię interesuje?
              </Text>
            }>
            <InputNumber min={0} placeholder="np. 50" className="w-full" addonAfter="m²" />
          </Form.Item>

          <Form.Item
            label={t('FORM.DESCRIPTION_LABEL')}
            name="description"
            rules={[{ max: 500, message: t('FORM.DESCRIPTION_MAX_LENGTH') }]}>
            <Input.TextArea placeholder={t('FORM.DESCRIPTION_PLACEHOLDER')} rows={4} showCount maxLength={500} />
          </Form.Item>

          <Form.Item
            label={t('FORM.WEBSITE_LABEL')}
            name="companyWebsite"
            rules={[
              {
                type: 'url',
                message: t('FORM.WEBSITE_INVALID'),
              },
            ]}
            extra={
              <Text type="secondary" className="text-xs">
                Pomóż wynajmującym poznać Twoją firmę - link będzie widoczny w ogłoszeniu
              </Text>
            }>
            <Input placeholder="https://www.example.com" size="large" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
              className="w-full">
              {mode === 'edit' ? t('FORM.SUBMIT_BUTTON_EDIT') : t('FORM.SUBMIT_BUTTON_ADD')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};
